import React, { useContext, Fragment, useEffect, useState } from "react";
import TestResult from "./TestResult";
import Link from "../Link";
import Expander from "../Expander";
import SelectedTestsContext, { useSelectedTests } from "./contexts/SelectedTestsContext";
import TestResultsContext, { useTestResultsContext } from "./contexts/TestResultsContext";
import SampleComments from "../registration/SampleComments";
import {
  ElipsisTd,
  BiggerElipsisTd,
  CenterTd,
  Td,
  CaveatTd,
  LimitSpan,
  EmptyTd,
  WaitingForResult,
} from "./theme/Theme";
import styled, { css } from "styled-components";
import { useTests } from "./contexts/ResultsContext";
import SampleColumns from "./SampleColumns";
import SampleView from "../sample_reporting/SampleView";

export default function SampleTestRowWrapper({
  sample,
  sample_test,
  selected,
  showSample,
  showTechnique,
  collapseTechnique,
  clientView = true,
  viewSingleSample,
  simpleSampleView,
}) {
  const {
    dispatch,
    state: { selectedResultCaveatIds, selectedSampleIds },
  } = useContext(SelectedTestsContext);
  const {
    state: { addingSampleTestIds },
  } = useContext(TestResultsContext);
  const isSampleSelected = selectedSampleIds.includes(sample.sample.identifier);
  const isAddingResult = addingSampleTestIds.includes(sample_test.id);
  return (
    <MemoSampleTestRow
      dispatch={dispatch}
      clientView={clientView}
      sample={sample}
      sample_test={sample_test}
      showSample={showSample}
      showTechnique={showTechnique}
      collapseTechnique={collapseTechnique}
      selectedResultCaveatIds={selectedResultCaveatIds}
      selected={selected}
      isSampleSelected={isSampleSelected}
      isAddingResult={isAddingResult}
      viewSingleSample={viewSingleSample}
      simpleSampleView={simpleSampleView}
    />
  );
}

const SampleTestRow = function({
  clientView = true,
  sample,
  sample_test,
  selected,
  showSample,
  showTechnique,
  collapseTechnique,
  isAddingResult,
  isSampleSelected,
  selectedResultCaveatIds,
  dispatch,
  viewSingleSample,
  simpleSampleView,
}) {
  const tests = useTests();
  const test = tests[sample_test.test_id];
  const [showSampleDetails, setShowSampleDetails] = useState(false);
  const [sampleView, setSampleView] = useState("comments");

  function toggleTest() {
    dispatch({
      type: "toggle",
      sampleId: sample.sample.identifier,
      sampleTestId: sample_test.id,
    });
  }

  function toggleCaveat(e, resultCaveatId) {
    dispatch({
      type: "toggleCaveat",
      selected: e.target.checked,
      resultCaveatId: resultCaveatId,
    });
  }

  function handleSelectSample(e) {
    dispatch({
      type: "selectSample",
      selected: e.target.checked,
      sampleId: sample.sample.identifier,
    });
  }

  const caveats = sample_test.caveats || [];

  return (
    <Fragment>
      <tr>
        <SampleColumns
          sample={sample}
          showSample={showSample}
          clientView={clientView}
          handleSelectSample={handleSelectSample}
          isSampleSelected={isSampleSelected}
          viewSingleSample={viewSingleSample}
          simpleSampleView={simpleSampleView}
          toggleComments={() => {
            setShowSampleDetails(!showSampleDetails);
            setSampleView("comments");
          }}
          toggleRetests={() => {
            setShowSampleDetails(!showSampleDetails);
            setSampleView("sample_retests");
          }}
        />
        {showTechnique && (
          <ElipsisTd>
            {collapseTechnique ? (
              <Link onClick={collapseTechnique}>
                <Expander expanded />
                {test.technique_name}
              </Link>
            ) : (
              test.technique_name
            )}
          </ElipsisTd>
        )}
        {!showTechnique && <EmptyTd />}
        <BiggerElipsisTd>
          {!clientView &&
            sample_test.h && <i className="fa fa-pause" style={{ marginRight: 5 }} />}
          {test.test_name}
        </BiggerElipsisTd>
        <td>{test.accreditation_name}</td>
        {isAddingResult ? (
          <TestResult
            test={test}
            sample_test={sample_test}
            sample_id={sample.sample.identifier}
          />
        ) : sample_test.test_result_id ? (
          <DisplayResult sample_test={sample_test} test={test} />
        ) : (
          <WaitingForResult colSpan="5">waiting for a result</WaitingForResult>
        )}
        <ElipsisTd>
          {sample_test.limit_types &&
            sample_test.limit_types.map((lt) =>
              lt.limits.map((limit, i) => (
                <LimitSpan key={i} type={limit.type}>
                  {limit.formatted}
                </LimitSpan>
              ))
            )}
        </ElipsisTd>
        {!clientView && (
          <CenterTd>
            {!clientView &&
              sample_test.h && <i className="fa fa-pause" style={{ marginRight: 5 }} />}
            <input type="checkbox" onChange={toggleTest} checked={selected} />
          </CenterTd>
        )}
      </tr>
      {showSampleDetails && (
        <tr>
          <td colSpan={simpleSampleView ? 10 : 16}>
            <div style={{ padding: 10 }}>
              <SampleView
                key={sampleView}
                reloadSamples={() => {}}
                reloadSample={() => {}}
                sample={sample.sample}
                clientView={clientView}
                initialView={sampleView}
                onClose={() => setShowSampleDetails(false)}
              />
            </div>
          </td>
        </tr>
      )}
      {caveats.length > 0 && (
        <tr>
          <EmptyTd colSpan={simpleSampleView ? 3 : 9} />
          <CaveatTd colSpan={6}>
            <ul>
              {caveats.map((c) => {
                var caveatString = c.text.substring(0, 180);
                if (caveatString.length >= 180) {
                  caveatString += "...";
                }
                return (
                  <li key={c.id}>
                    <input
                      type="checkbox"
                      onChange={(e) => toggleCaveat(e, c.id)}
                      checked={selectedResultCaveatIds.includes(c.id)}
                    />
                    &nbsp;
                    {caveatString}
                  </li>
                );
              })}
            </ul>
          </CaveatTd>
        </tr>
      )}
    </Fragment>
  );
};

function DisplayResult({ sample_test, test }) {
  return (
    <Fragment>
      <Td reviewed={sample_test.reviewed}>{test.lod_with_units}</Td>
      <Td
        reviewed={sample_test.reviewed}
        failed={sample_test.failed}
        warning={sample_test.warning}
        colSpan="2"
      >
        {sample_test.formatted_result}
      </Td>
      <Td reviewed={sample_test.reviewed}>{sample_test.test_name}</Td>
      <Td reviewed={sample_test.reviewed}>{sample_test.reviewed ? "R" : "P"}</Td>
    </Fragment>
  );
}

const MemoSampleTestRow = React.memo(SampleTestRow);
